import axios from 'axios';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import './UpdateConferenceDate.css';

const UpdateConferenceDate = () => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const form = event.target;

        const dateData = {
            PaperSubmissionDeadline: form.PaperSubmissionDeadline.value,
            AcceptanceNotification: form.AcceptanceNotification.value,
            CameraReadySubmission: form.CameraReadySubmission.value,
            RegistrationDeadline: form.RegistrationDeadline.value,
            ConferenceStartDate: form.ConferenceStartDate.value,
            ConferenceEndDate: form.ConferenceEndDate.value,
        };

        // Validate that end date is not before start date
        if (new Date(dateData.ConferenceEndDate) < new Date(dateData.ConferenceStartDate)) {
            Swal.fire({
                icon: 'warning',
                title: 'Invalid Date Range',
                text: 'Conference end date cannot be before the start date.',
                confirmButtonColor: '#5e71f6',
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/update-date`,
                dateData
            );
            if (response.status === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Updated!',
                    text: 'Important dates have been updated successfully!',
                    confirmButtonColor: '#5e71f6',
                });
                navigate('/dates');
            }
        } catch (error) {
            console.error('Error updating dates:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'An error occurred while updating the dates. Please try again.',
                confirmButtonColor: '#5e71f6',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="update-conference-date-wrapper">
            <div className="update-conference-date-container">
                {/* Header Section */}
                <div className="update-date-header">
                    <h1 className="update-date-title">Update Important Dates</h1>
                    <p className="update-date-subtitle">Manage key deadlines and conference dates</p>
                </div>

                {/* Form Card */}
                <div className="date-form-card">
                    <div className="date-form-header">
                        <h2 className="form-card-title">
                            <svg className="form-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Conference Timeline
                        </h2>
                    </div>

                    <form onSubmit={handleSubmit} className="date-form">
                        {/* Paper Submission Deadline */}
                        <div className="form-field">
                            <label className="form-label">
                                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Paper Submission Deadline <span className="required-mark">*</span>
                            </label>
                            <input
                                required
                                name="PaperSubmissionDeadline"
                                type="date"
                                className="form-input"
                                id="PaperSubmissionDeadline"
                            />
                        </div>

                        {/* Acceptance Notification */}
                        <div className="form-field">
                            <label className="form-label">
                                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Acceptance Notification <span className="required-mark">*</span>
                            </label>
                            <input
                                required
                                name="AcceptanceNotification"
                                type="date"
                                className="form-input"
                                id="AcceptanceNotification"
                            />
                        </div>

                        {/* Camera Ready Submission */}
                        <div className="form-field">
                            <label className="form-label">
                                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                Camera Ready Submission <span className="required-mark">*</span>
                            </label>
                            <input
                                required
                                name="CameraReadySubmission"
                                type="date"
                                className="form-input"
                                id="CameraReadySubmission"
                            />
                        </div>

                        {/* Registration Deadline */}
                        <div className="form-field">
                            <label className="form-label">
                                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                                </svg>
                                Registration Deadline <span className="required-mark">*</span>
                            </label>
                            <input
                                required
                                name="RegistrationDeadline"
                                type="date"
                                className="form-input"
                                id="RegistrationDeadline"
                            />
                        </div>

                        {/* Conference Date Range */}
                        <div className="form-field">
                            <label className="form-label">
                                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                Conference Date <span className="required-mark">*</span>
                            </label>
                            <div className="date-range-container">
                                <div className="date-range-field">
                                    <label className="date-range-label">Start Date</label>
                                    <input
                                        required
                                        name="ConferenceStartDate"
                                        type="date"
                                        className="form-input date-range-input"
                                        id="ConferenceStartDate"
                                    />
                                </div>
                                <div className="date-separator-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                </div>
                                <div className="date-range-field">
                                    <label className="date-range-label">End Date</label>
                                    <input
                                        required
                                        name="ConferenceEndDate"
                                        type="date"
                                        className="form-input date-range-input"
                                        id="ConferenceEndDate"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Info Box */}
                        <div className="info-box">
                            <svg className="info-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="info-content">
                                <strong>Note:</strong> All dates will be displayed publicly on the conference website. Ensure all deadlines are accurate before updating.
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button 
                            type="submit" 
                            className="submit-button"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <svg className="button-icon spinner" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <svg className="button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Update Dates
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default UpdateConferenceDate;
